import {
  LocalizedDatePicker,
  LocalizedDateTimePicker,
} from '@components/fields/localizedDatePicker';
import type { CustomFieldFormats } from '@interfaces/customField';
import { Button } from '@mui/material';
import type { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { UserContext } from '@providers/userContextProvider';
import dayjs from 'dayjs';
import { Grid } from 'hephaestus/web';
import { isNil } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '../textField/textField';

type DateTimeProps = Omit<
  Partial<React.ComponentProps<typeof LocalizedDateTimePicker>>,
  'type'
> &
  Omit<React.ComponentProps<typeof TextField>, 'type'> & {
    type: 'DATE_TIME';
  };

type DatePickerProps = Partial<
  React.ComponentProps<typeof LocalizedDatePicker>
> &
  Omit<React.ComponentProps<typeof TextField>, 'type'> & {
    type?: React.ComponentProps<typeof TextField>['type'] | CustomFieldFormats;
  };

interface DateFieldActionBarProps extends PickersActionBarProps {
  currentPickerValue?: dayjs.Dayjs | null;
  onResetValue?: () => void;
}

const DateFieldActionBar = ({
  onAccept,
  onCancel,
  currentPickerValue,
  onResetValue,
}: DateFieldActionBarProps) => {
  const { t } = useTranslation('components');
  const isDateValid = currentPickerValue && dayjs(currentPickerValue).isValid();

  return (
    <Grid
      container
      sx={{
        justifyContent: 'flex-end',
        p: 1,
      }}
    >
      <Button
        onClick={() => {
          onResetValue?.();
          onCancel();
        }}
      >
        {t('modelPortfolios:portfolio.edit.cancel')}
      </Button>
      <Button onClick={() => onAccept()} disabled={!isDateValid}>
        {t('modelPortfolios:portfolio.ok')}
      </Button>
    </Grid>
  );
};

export const DateField = (
  props: (DateTimeProps | DatePickerProps) & {
    onCancel?: () => void;
    dataTestId?: string;
    locked?: boolean;
    hideTimeContainer?: boolean;
    autocomplete?: React.ComponentProps<typeof TextField>['autocomplete'];
    lockMessage?: string;
  },
) => {
  const { t } = useTranslation('pageConfiguration');
  const { activeOrganization } = useContext(UserContext);
  const { dataTestId, ...otherProps } = props;
  const isDisabled = props.disabled || props.locked;
  const [error, setError] = useState<boolean | undefined>();
  const [currentPickerValue, setCurrentPickerValue] = useState<any>(
    props.value || null,
  );

  const dateFormat =
    activeOrganization.applicableLocalization?.dateFormat ?? 'YYYY-MM-DD';
  const maxDateLength = dayjs().format(dateFormat).length;

  useEffect(() => {
    setCurrentPickerValue(props.value || null);
  }, [props.value]);

  if (props.type === 'DATE_TIME') {
    return (
      <LocalizedDateTimePicker
        label={props.label}
        value={currentPickerValue}
        disabled={isDisabled}
        openTo={props.openTo ?? 'year'}
        onChange={(date) => props.onChange && props.onChange(date)}
        onAccept={(date) => props.onAccept && props.onAccept(date)}
        onClose={() => setError(false)}
        renderInput={(params: any) => (
          <TextField
            {...params}
            {...otherProps}
            autocomplete={props.autocomplete ?? 'on'}
            testId={dataTestId ?? 'custom-datepicker'}
            trailingIcon={isDisabled ? undefined : 'calendar'}
            locked={isDisabled}
            lockMessage={
              props.lockMessage ?? t('pageConfiguration:notEditableMessage')
            }
            onChange={(e: any) => {
              setError(undefined);
              if (e.target.value.length <= maxDateLength) {
                params.onChange?.(e);
                otherProps.onChange?.(e);
              }
            }}
            {...(!isNil(error) && !isNil(props.error) && { error })}
          />
        )}
        components={{
          ActionBar: DateFieldActionBar,
        }}
        componentsProps={{
          actionBar: {
            currentPickerValue,
            onResetValue: () => {
              setCurrentPickerValue(props.value || null);
              if (props.onCancel) {
                props.onCancel();
              }
            },
          } as any,
        }}
        minDate={props.minDate}
        shouldDisableDate={props.shouldDisableDate}
        DialogProps={{
          sx: {
            ...(props.hideTimeContainer
              ? {
                  '.MuiDateTimePickerToolbar-timeContainer': {
                    display: 'none',
                  },
                }
              : {}),
          },
        }}
      />
    );
  }

  return (
    <LocalizedDatePicker
      label={props.label}
      value={currentPickerValue}
      disabled={isDisabled}
      openTo={props.openTo ?? 'year'}
      views={props.views ?? ['year', 'month', 'day']}
      customFormat={props.customFormat}
      onChange={(date) => props.onChange && props.onChange(date)}
      onAccept={(date) => props.onAccept && props.onAccept(date)}
      onOpen={() => props.onOpen && props.onOpen()}
      onClose={() => setError(false)}
      renderInput={(params: any) => (
        <TextField
          {...params}
          {...otherProps}
          autocomplete={props.autocomplete ?? 'on'}
          testId={dataTestId ?? 'custom-datepicker'}
          trailingIcon={isDisabled ? undefined : 'calendar'}
          locked={isDisabled}
          onChange={(e: any) => {
            setError(undefined);
            if (e.target.value.length <= maxDateLength) {
              params.onChange?.(e);
              otherProps.onChange?.(e);
            }
          }}
          lockMessage={
            props.lockMessage ?? t('pageConfiguration:notEditableMessage')
          }
          {...(!isNil(error) && { error })}
        />
      )}
      components={{
        ActionBar: DateFieldActionBar,
      }}
      componentsProps={{
        actionBar: {
          currentPickerValue,
          onResetValue: () => {
            setCurrentPickerValue(props.value || null);
            if (props.onCancel) {
              props.onCancel();
            }
          },
        } as any,
      }}
      minDate={props.minDate}
      shouldDisableDate={props.shouldDisableDate}
      maxDate={props.maxDate}
    />
  );
};

export default DateField;
